import { useEffect, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import NotesList from './NotesList';

const SCROLL_SPEEDS = { slow: 20, medium: 45, fast: 90 }; // px/s
const SPEED_CYCLE = ['slow', 'medium', 'fast'];
let _sessionScrollSpeed = 'medium';

export default function PdfViewerModal({
  song,
  resolvedUrl,
  onClose,
  initialTab = "sheet_music",
  tenant,
  currentUserId,
  currentUserName,
  userRole,
  onNoteCountChange,
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(_sessionScrollSpeed);

  const [pdfBaseHeight, setPdfBaseHeight] = useState(null);

  const scrollContainerRef = useRef(null);
  const animRef = useRef(null);
  const lastTimeRef = useRef(null);
  const scrollSpeedRef = useRef(_sessionScrollSpeed);
  const pdfBaseHeightRef = useRef(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    scrollSpeedRef.current = scrollSpeed;
    _sessionScrollSpeed = scrollSpeed;
  }, [scrollSpeed]);

  useEffect(() => {
    pdfBaseHeightRef.current = pdfBaseHeight;
  }, [pdfBaseHeight]);

  useEffect(() => {
    if (!scrolling) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      lastTimeRef.current = null;
      return;
    }
    function tick(ts) {
      if (lastTimeRef.current === null) lastTimeRef.current = ts;
      const dt = (ts - lastTimeRef.current) / 1000;
      lastTimeRef.current = ts;
      const el = scrollContainerRef.current;
      if (el) {
        const ph = pdfBaseHeightRef.current;
        const maxScroll = ph
          ? Math.max(0, ph - el.clientHeight)
          : Math.min(el.scrollHeight - el.clientHeight, el.clientHeight * 5);
        if (el.scrollTop >= maxScroll - 1) {
          setScrolling(false);
          return;
        }
        el.scrollTop += SCROLL_SPEEDS[scrollSpeedRef.current] * dt;
      }
      animRef.current = requestAnimationFrame(tick);
    }
    animRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      lastTimeRef.current = null;
    };
  }, [scrolling]);

  useEffect(() => {
    setScrolling(false);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [resolvedUrl]);

  useEffect(() => {
    if (!resolvedUrl) return;
    let cancelled = false;
    setPdfBaseHeight(null);

    async function measurePdf() {
      try {
        const res = await fetch(resolvedUrl);
        const bytes = await res.arrayBuffer();
        const pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const pages = pdfDoc.getPages();
        if (cancelled) return;
        const containerEl = scrollContainerRef.current;
        const containerWidth = containerEl ? containerEl.clientWidth : window.innerWidth;
        let totalHeight = 0;
        for (const page of pages) {
          const { width, height: pageH } = page.getSize();
          totalHeight += (pageH / width) * containerWidth;
        }
        setPdfBaseHeight(totalHeight);
      } catch {
        // keep null → fall back to default height
      }
    }

    measurePdf();
    return () => { cancelled = true; };
  }, [resolvedUrl]);

  function handleTabChange(tab) {
    setActiveTab(tab);
    setZoomLevel(1);
    setScrolling(false);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await fetch(resolvedUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${song.title}.pdf`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setDownloading(false);
    }
  }

  const iframeSrc = resolvedUrl ? `${resolvedUrl}#toolbar=0&navpanes=0&scrollbar=0` : resolvedUrl;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/70"
      onClick={onClose}
    >
      {/* Header — single merged row */}
      <div
        className="flex h-12 flex-shrink-0 items-stretch border-b border-border bg-canvas"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title + artist */}
        <div className="flex min-w-0 items-center gap-3 px-2 sm:px-4">
          <span className="truncate text-[15px] font-semibold tracking-[-0.01em] text-text-primary">
            {song.title}
          </span>
          {song.artist && (
            <span className="hidden sm:inline text-xs text-text-muted">{song.artist}</span>
          )}
        </div>

        {/* Tab buttons */}
        <div className="flex flex-shrink-0 items-end gap-1">
          <button
            onClick={() => handleTabChange("sheet_music")}
            className={[
              'px-2 pb-2 text-sm font-medium transition-colors sm:px-3',
              activeTab === "sheet_music"
                ? 'border-b-2 border-accent-blue text-text-primary'
                : 'text-text-muted hover:text-text-secondary',
            ].join(' ')}
          >
            Sheet music
          </button>
          <button
            onClick={() => handleTabChange("notes")}
            className={[
              'px-2 pb-2 text-sm font-medium transition-colors sm:px-3',
              activeTab === "notes"
                ? 'border-b-2 border-accent-blue text-text-primary'
                : 'text-text-muted hover:text-text-secondary',
            ].join(' ')}
          >
            Notes{song.noteCount > 0 ? ` (${song.noteCount})` : ''}
          </button>
        </div>

        <div className="flex-1" />

        {/* Right controls */}
        <div className="flex flex-shrink-0 items-center gap-3 px-2 sm:px-4">
          {/* Download button */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            aria-label="Download PDF"
            className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary disabled:opacity-50"
          >
            {downloading ? (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="25 10" />
              </svg>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M8 2v8m0 0L5 7m3 3l3-3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 12h12" strokeLinecap="round" />
              </svg>
            )}
            <span className="hidden sm:inline">{downloading ? 'Downloading…' : 'Download'}</span>
          </button>

          {/* Zoom controls */}
          {activeTab === "sheet_music" && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.25))}
                disabled={zoomLevel <= 0.5}
                className="flex h-7 w-7 items-center justify-center rounded border border-border text-text-muted hover:text-text-primary disabled:opacity-40"
                aria-label="Zoom out"
              >
                −
              </button>
              <span className="w-10 text-center text-xs tabular-nums text-text-muted">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={() => setZoomLevel(z => Math.min(3, z + 0.25))}
                disabled={zoomLevel >= 3}
                className="flex h-7 w-7 items-center justify-center rounded border border-border text-text-muted hover:text-text-primary disabled:opacity-40"
                aria-label="Zoom in"
              >
                +
              </button>
            </div>
          )}

          {/* Auto-scroll controls */}
          {activeTab === "sheet_music" && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setScrolling(s => !s)}
                aria-label={scrolling ? "Pause auto-scroll" : "Start auto-scroll"}
                className="flex h-7 w-7 items-center justify-center rounded border border-border text-text-muted hover:text-text-primary"
              >
                {scrolling ? '⏸' : '▶'}
              </button>
              <button
                onClick={() => setScrollSpeed(s => SPEED_CYCLE[(SPEED_CYCLE.indexOf(s) + 1) % SPEED_CYCLE.length])}
                aria-label="Cycle scroll speed"
                className="flex h-7 w-6 items-center justify-center rounded border border-border text-[10px] font-medium text-text-muted hover:text-text-primary"
              >
                {scrollSpeed[0].toUpperCase()}
              </button>
            </div>
          )}

          <button
            onClick={onClose}
            className="ml-2 text-sm text-text-muted hover:text-text-primary"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "sheet_music" ? (
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            src={iframeSrc}
            title={song.title}
            className="border-0"
            style={{
              width: zoomLevel === 1 ? '100%' : `${zoomLevel * 100}%`,
              height: `max(600vh, ${zoomLevel * 100}%)`,
              minHeight: '600vh',
              pointerEvents: 'none',
            }}
          />
        </div>
      ) : (
        <div
          className="flex-1 overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <NotesList
            songId={song.id}
            tenant={tenant}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            userRole={userRole}
            onNoteCountChange={onNoteCountChange}
          />
        </div>
      )}
    </div>
  );
}
